import { useCallback, useState } from "react";

interface UseFeedbackFormOptions {
  submit: (score: number, comment: string) => Promise<Response>;
  onClose: () => void;
  onDismiss?: () => void;
}

export const useFeedbackForm = ({
  submit,
  onClose,
  onDismiss,
}: UseFeedbackFormOptions) => {
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(false);

  const resetState = useCallback(() => {
    setScore(null);
    setComment("");
    setIsSubmitted(false);
    setError(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (score === null) return;
    setIsSubmitting(true);
    setError(false);
    try {
      const response = await submit(score, comment.trim());
      if (response.ok) {
        setIsSubmitted(true);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [score, comment, submit]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
      resetState();
      onDismiss?.();
    }
  }, [isSubmitting, onClose, onDismiss, resetState]);

  return {
    score,
    setScore,
    comment,
    setComment,
    isSubmitting,
    isSubmitted,
    error,
    handleSubmit,
    handleClose,
  };
};
